import { toFloatInt } from 'helpers/NumberFormats';
import { createProductIdentifiersProto } from 'helpers/analytics';
import { PRODUCT_ASIN } from 'common/regex';
import { COLLECTIONS_LIST_PAGE, COLLECTIONS_PAGE } from 'constants/amethystPageTypes';
import { middlewareTrack } from 'apis/amethyst';

/**
  * https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/AddToFavorites.proto
  *
  * @param {string} productIdentifiers - Keys: productId, styleId, colorId, stockId, asin
  * @param {integer/float} price - price with cents, ex: 10.99. toFloatInt in helpers/NumbersFormats.js
  * may be of use.
  * @param {bool} incompleteAddToFavorites - Add to cart clicked without size/width/some option selected. Empty if add to cart doesn't fail. Bool determines if add to cart occurred or not. Dimension indicates which dimension was not selected.
  * @param {string} missingDimension - see WebsiteEnums.proto for ProductDimension values.
  * Current values are: UNKNOWN_PRODUCT_DIMENSION, SIZE_DIMENSION, WIDTH_DIMENSION, INSEAM_DIMENSION
  * @param {string} addedFrom - see WebsiteEnums.proto for PageType values. Examples:
  * SEARCH_PAGE, PRODUCT_PAGE, HOMEPAGE, LANDING_PAGE, CART_PAGE, CHECKOUT_PAGE
*/
export const evAddToFavorites = ({ productId, styleId, colorId, stockId, asin, price, incompleteAddToFavorites, missingDimension, addedFrom }) => ({
  addToFavorites: {
    productIdentifiers: {
      productId,
      styleId,
      colorId,
      stockId,
      asin
    },
    price: price ? toFloatInt(price) : undefined,
    incompleteAddToFavorites,
    missingDimension,
    addedFrom
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/FavoritesPageView.proto
export const pvFavorites = hearts => ({
  favoritesPageView: {
    itemsInFavorites: hearts.length,
    favoritedProducts: createProductIdentifiersProto(hearts)
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/ProductClickthrough.proto
export const evProductClickthrough = ({ asin, productId, styleId, colorId, stockId }) => ({
  productClickthrough: {
    clickedProduct: {
      asin,
      productId,
      styleId,
      colorId,
      stockId
    },
    sourcePage: 11 // pageType as defined https://code.amazon.com/packages/AmethystEvents/blobs/faf0f5e028991bc37ab4f812b597685205aa5bfc/--/configuration/include/com/zappos/amethyst/website/WebsiteEnums.proto#L86
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/ProductNotifyMe.proto
export const evProductNotifyMe = ({ asin, productId, styleId, colorId, stockId }) => ({
  productNotifyMe: {
    productIdentifiers: {
      asin,
      productId,
      styleId,
      colorId,
      stockId
    },
    sourcePage: 11 // pageType as defined https://code.amazon.com/packages/AmethystEvents/blobs/faf0f5e028991bc37ab4f812b597685205aa5bfc/--/configuration/include/com/zappos/amethyst/website/WebsiteEnums.proto#L86
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/RemoveFromFavorites.proto
export const evRemoveFromFavorites = ({ asin, productId, styleId, colorId, stockId, sourcePage = 11 }) => ({
  removeFromFavorites: {
    productIdentifiers: {
      asin,
      productId,
      styleId,
      colorId,
      stockId
    },
    sourcePage // pageType as defined https://code.amazon.com/packages/AmethystEvents/blobs/faf0f5e028991bc37ab4f812b597685205aa5bfc/--/configuration/include/com/zappos/amethyst/website/WebsiteEnums.proto#L86
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/SelectSizeButton.proto
export const evSelectSizeButton = ({ asin, productId, styleId, colorId, stockId }) => ({
  selectSizeButton: {
    productIdentifiers: {
      asin,
      productId,
      styleId,
      colorId,
      stockId
    },
    sourcePage: 11 // pageType as defined https://code.amazon.com/packages/AmethystEvents/blobs/faf0f5e028991bc37ab4f812b597685205aa5bfc/--/configuration/include/com/zappos/amethyst/website/WebsiteEnums.proto#L86
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/AddToCart.proto
export const evAddToCart = ({ asin, price, productId, styleId, colorId, stockId }) => ({
  addToCart: {
    productIdentifiers: {
      asin,
      productId,
      styleId,
      colorId,
      stockId
    },
    price,
    addedFrom: 11 // pageType as defined https://code.amazon.com/packages/AmethystEvents/blobs/faf0f5e028991bc37ab4f812b597685205aa5bfc/--/configuration/include/com/zappos/amethyst/website/WebsiteEnums.proto#L86
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/AddToCollections.proto
export const evAddToCollections = ({ asin, productId, styleId, colorId, stockId, price, collectionId, incompleteAddToCollections, missingDimension, sourcePage = COLLECTIONS_PAGE }) => ({
  addToCollections: {
    productIdentifiers: {
      asin,
      productId,
      styleId,
      colorId,
      stockId
    },
    price,
    incompleteAddToCollections,
    missingDimension,
    collectionId,
    sourcePage // defaults to 13, but can fire on PDP (2), or Search (3) or anywhere where a Reco is (maybe we can use UNKNOWN_PAGE_TYPE for that?)
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/RemoveFromCollections.proto
export const evRemoveFromCollections = ({ asin, productId, styleId, colorId, stockId, price, collectionId, sourcePage = COLLECTIONS_PAGE }) => ({
  removeFromCollection: { // notice there's an s missing on this property name https://code.amazon.com/packages/AmethystEvents/blobs/a6ee4162eb4e615f995c41f5e4242584ccb2d30d/--/configuration/include/com/zappos/amethyst/website/WebsiteEvent.proto#L211
    productIdentifiers: {
      asin,
      productId,
      styleId,
      colorId,
      stockId
    },
    price,
    collectionId,
    sourcePage
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/AllCollectionsPageView.proto
export const pvCollections = ({ collectionCount, recommendationImpression, isOwner }) => ({
  allCollectionsPageView: {
    collectionCount,
    recommendationImpression,
    isOwner,
    sourcePage: COLLECTIONS_LIST_PAGE
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/CollectionsPageView.proto
export const pvCollection = ({ itemCount, recommendationImpression, productsInCollection, isOwner, arrivedFromShareLink, collectionId }) => ({
  collectionsPageView: {
    itemCount,
    recommendationImpression,
    productsInCollection,
    isOwner,
    arrivedFromShareLink,
    collectionId,
    sourcePage: COLLECTIONS_PAGE
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/CreateCollectionClick.proto
const evCreateCollectionClick = ({ collectionId, collectionName }) => ({
  createCollectionClick: {
    collectionId,
    collectionName,
    sourcePage: COLLECTIONS_LIST_PAGE
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/DeleteCollectionClick.proto
const evDeleteCollectionClick = ({ collectionId }) => ({
  deleteCollectionClick: {
    collectionId,
    sourcePage: COLLECTIONS_LIST_PAGE
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/EditCollectionClick.proto
export const evEditCollectionClick = ({ collectionId }) => ({
  editCollectionClick: {
    collectionId,
    sourcePage: COLLECTIONS_LIST_PAGE
  }
});

// https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/ShareCollectionClick.proto
const evShareCollectionClick = ({ collectionId, sourcePage }) => ({
  shareCollectionClick: {
    collectionId,
    sourcePage
  }
});

export const addSubItemId = (payload, subItemId) => {
  // no asin/stockId so just return payload
  if (!subItemId) {
    return payload;
  }

  const IDTYPE = PRODUCT_ASIN.test(subItemId) ? 'asin' : 'stockId';
  payload[IDTYPE] = IDTYPE === 'stockId' ? parseInt(subItemId, 10) : subItemId;

  return payload;
};

export const DELETE_COLLECTION_LIST = 'DELETE_COLLECTION_LIST';
export const SHARE_COLLECTION_LIST = 'SHARE_COLLECTION_LIST';
export const CREATE_COLLECTION_LIST = 'CREATE_COLLECTION_LIST';
export const REMOVE_FROM_COLLECTION = 'REMOVE_FROM_COLLECTION';
export const COLLECTIONS_PAGE_VIEW = 'COLLECTIONS_PAGE_VIEW';

export default {
  pageEvent: COLLECTIONS_PAGE_VIEW,
  events: {
    [COLLECTIONS_PAGE_VIEW]: [], // deliberately leaving this blank as we need `pageEvent` but were dispatching page views manually due to `FavoritesPicker.jsx`. Will clean up once `soc` goes permanent
    [DELETE_COLLECTION_LIST]: [(_, params) => middlewareTrack(evDeleteCollectionClick(params))],
    [SHARE_COLLECTION_LIST]: [(_, params) => middlewareTrack(evShareCollectionClick(params))],
    [CREATE_COLLECTION_LIST]: [(_, params) => middlewareTrack(evCreateCollectionClick(params))]
  }
};
